<div class="container">
  <div class="heading py-5">
    <h1 class="bold font-curve-large">Intranet Header</h1>
  </div>

  <h2 class="bold">Installation</h2>
  <p class="light font-curve-regular">
    The Intranet Header is an
    <a href="https://angular.io/">Angular</a>
    component and as such, only works with Angular projects.
  </p>
  <div class="table-responsive">
    <table class="table caption-top mb-regular-r">
      <caption>
        List of compatible version numbers with external dependencies.
      </caption>
      <thead>
        <tr>
          <th scope="col">Intranet-header</th>
          <th scope="col">Bootstrap</th>
          <th scope="col">Angular</th>
          <th scope="col">Ng-Bootstrap</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <th scope="row">3.x</th>
          <td>4.x</td>
          <td>14.x</td>
          <td>13.x</td>
        </tr>
        <tr>
          <th scope="row">4.x</th>
          <td>5.2</td>
          <td>15.x</td>
          <td>14.x</td>
        </tr>
        <tr>
          <th scope="row">5.x</th>
          <td>5.2</td>
          <td>16.x</td>
          <td>15.x</td>
        </tr>
      </tbody>
    </table>
  </div>
  <p>Install the latest version of the Intranet Header with:</p>
  <code
    class="block mb-regular-r bash"
    [highlight]="'npm install @swisspost/design-system-intranet-header' | formatCode"
    [languages]="['shell']"
  ></code>
  <p>Or specify the version you need:</p>
  <code
    class="block mb-regular-r bash"
    [highlight]="'npm install @swisspost/design-system-intranet-header@4' | formatCode"
    [languages]="['shell']"
  ></code>

  <h2 class="bold mt-5">Usage</h2>
  <section class="light font-curve-regular">
    <p>
      In your
      <code>app.module.ts</code>
      , add the header to your imports:
    </p>
    <code class="block" [highlight]="codeModule" [languages]="['typescript']"></code>
    <p>
      On your layout page; create a container (e.g.
      <code>&lt;div&gt;</code>
      or
      <code>&lt;section&gt;</code>
      - or use the
      <code>body</code>
      container) and apply the following css classes:
    </p>
    <ul>
      <li><code>layout-container</code></li>
      <li>
        <code>header-big</code>
        - if you are using the top with the navbar
      </li>
      <li>
        <code>header-small</code>
        - if you are using the top without the navbar
      </li>
      <li>
        <code>with-searchbar</code>
        - if you are using the top with the searchbar
      </li>
    </ul>
    <p>
      Inside, add the
      <code>post-intranet-header</code>
      component, with the applicable options (see below).
    </p>
    <p>
      If you are using the navbar, add the list-items for the navigation directly inside the tag.
      (see example "With top navigation, without sidenav")
    </p>
    <p>
      If you are using a side-nav, adjust your markup accordingly. (See examples "Without top
      navigation, with sidenav" and "Intranet-Header "postweb"").
      <br />
      Make sure that you have the
      <code>openedMenu</code>
      Property in your angular app and that you apply the same classes to the markup as shown in the
      mentioned examples.
    </p>
  </section>

  <h3 class="bold mt-5" id="intranet-layout-options">Options</h3>
  <div class="table-responsive-sm mt-3">
    <table
      class="table table-striped table-bordered table-sm"
      aria-describedby="intranet-layout-options"
    >
      <thead class="thead-dark">
        <tr>
          <th scope="col">Property</th>
          <th scope="col">Type</th>
          <th scope="col">Default</th>
          <th scope="col">Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>additionalInfo</code>
          </td>
          <td>
            <code>string</code>
          </td>
          <td>
            <code>''</code>
          </td>
          <td>
            Additional information about the user, shown as item in the user-dropdown.
            <br />
            By default, the item will not be rendered.
          </td>
        </tr>
        <tr>
          <td>
            <code>currentUserId</code>
          </td>
          <td>
            <code>string</code>
          </td>
          <td>
            <code>''</code>
          </td>
          <td>
            The user ID of the currently logged on user.
            <br />
            Used to show the users profile image.
            <br />
            The default value of
            <code>'user'</code>
            is used to show the fallback-image.
          </td>
        </tr>
        <tr>
          <td>
            <code>displayName</code>
          </td>
          <td>
            <code>string</code>
          </td>
          <td>
            <code>''</code>
          </td>
          <td>
            The name of the logged on user, shown as item in the user-dropdown.
            <br />
            By default, the item will not be rendered.
          </td>
        </tr>
        <tr>
          <td>
            <code>hasNavbar</code>
          </td>
          <td>
            <code>boolean</code>
          </td>
          <td>
            <code>true</code>
          </td>
          <td>Whether the navigation is rendered or not.</td>
        </tr>
        <tr>
          <td>
            <code>languages</code>
          </td>
          <td>
            <code>string</code>
          </td>
          <td>
            <code>'de,fr,it,en'</code>
          </td>
          <td>
            A comma-separated string that specifies the languages available to the language-selector
            shown as items in the user-dropdown.
            <br />
            Possible language values are:
            <code>de</code>
            ,
            <code>fr</code>
            ,
            <code>it</code>
            and
            <code>en</code>
          </td>
        </tr>
        <tr>
          <td>
            <code>logoUrl</code>
          </td>
          <td>
            <code>string</code>
          </td>
          <td>
            <code>''</code>
          </td>
          <td>
            The url which is used for the
            <code>href</code>
            attribute on the logo.
          </td>
        </tr>
        <tr>
          <td>
            <code>optionDropdownContent</code>
          </td>
          <td><code>TemplateRef&lt;any&gt;</code></td>
          <td><code>null</code></td>
          <td>
            Represents an embedded angular template that can be used to instantiate embedded views.
            <br />
            Shown as item in the user-dropdown.
            <br />
            By default, the item will not be rendered.
          </td>
        </tr>
        <tr>
          <td>
            <code>optionHeaderContent</code>
          </td>
          <td><code>TemplateRef&lt;any&gt;</code></td>
          <td><code>null</code></td>
          <td>
            Represents an embedded angular template that can be used to instantiate embedded views.
            <br />
            Shown as custom content before the user-dropdown.
            <br />
            By default, the item will not be rendered.
          </td>
        </tr>
        <tr>
          <td>
            <code>searchUrl</code>
          </td>
          <td>
            <code>string</code>
          </td>
          <td>
            <code>''</code>
          </td>
          <td>
            Used as the form
            <code>action</code>
            attribute on the search form.
            <br />
            If
            <code>showIntranetSearch</code>
            is set to
            <code>true</code>
            the
            <code>searchUrl</code>
            must be set as well.
          </td>
        </tr>
        <tr>
          <td>
            <code>showIntranetSearch</code>
          </td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Whether the search is rendered or not.</td>
        </tr>
        <tr>
          <td>
            <code>siteTitle</code>
          </td>
          <td>
            <code>string</code>
          </td>
          <td>
            <code>''</code>
          </td>
          <td>The website/application name, shown next to the Logo.</td>
        </tr>
      </tbody>
    </table>
  </div>
  <h3 class="bold mt-5">Examples</h3>
  <h4 class="bold mt-5">With top navigation, without sidenav</h4>
  <p class="light font-curve-regular">
    Add
    <code class="bg-light">
      &lt;main role="main"&gt; &lt;!-- Site Content / Router-Outlet --&gt; &lt;/main&gt;
    </code>
    beneath.
  </p>
</div>

<div class="header-showcase-container p-huge-r">
  <div class="header-showcase-page">
    <iframe
      title="Intranet-header example with top navigation and without sidenav"
      src="/#/samples/intranet-layout-regular"
      class="w-100"
      style="min-height: 300px"
    ></iframe>
  </div>
</div>

<div class="container">
  <code
    class="block mt-3"
    [highlight]="codeTemplateReg"
    [languages]="['html', 'scss', 'css', 'typescript', 'javascript']"
  ></code>

  <h4 class="bold mt-5">Without top navigation, with sidenav</h4>
</div>

<div class="header-showcase-container p-huge-r">
  <div class="header-showcase-page">
    <iframe
      title="Intranet-header example without top navigation and with sidenav"
      src="/#/samples/intranet-layout-sidebar-small-sidebar"
      class="w-100"
      style="min-height: 500px"
    ></iframe>
  </div>
</div>

<div class="container">
  <span class="spacer"></span>
  <code
    class="block"
    [highlight]="codeTemplateSmall"
    [languages]="['html', 'scss', 'css', 'typescript', 'javascript']"
  ></code>

  <h4 class="bold mt-5">Intranet-Header "postweb"</h4>
  <div class="alert-container">
    <div class="alert alert-danger">
      <p>Top navigation links must be maintained by the application!</p>
    </div>
  </div>
</div>

<div class="header-showcase-container p-huge-r">
  <div class="header-showcase-page">
    <iframe
      title="Intranet-header 'postweb' example with top navigation and with sidenav"
      src="/#/samples/intranet-layout-sidebar-big-sidebar"
      class="w-100"
      style="min-height: 500px"
    ></iframe>
  </div>
</div>

<div class="container">
  <span class="spacer"></span>
  <code
    class="block"
    [highlight]="codeTemplateBig"
    [languages]="['html', 'scss', 'css', 'typescript', 'javascript']"
  ></code>
</div>
